import { justificativaParaEnvio } from "../../hooks/nova-obra.condicoes";
import { useRef, useState } from "react";
import { useToast } from "@/ds";
import { isAPIError } from "@/core/services";
import { criarObra, lerErroCriarObra, useObraTiposObra } from "@/modules/obras";
import { montarPayloadObra } from "../../hooks/nova-obra.payload";
import { readEquipe } from "../../hooks/nova-obra.equipe";
import { useCondicoesObra } from "../../hooks/use-condicoes-obra";
import { useNovaObra } from "../../hooks/nova-obra.context";
import type { NovaObraState } from "../../hooks/nova-obra.types";
import { readDivergenciasAlocacao } from "@/modules/recursos/equipe-obra.gateway";
import type { DivergenciasAlocacao } from "@/modules/recursos";
import { CRIAR_OBRA } from "../../nova-obra.constants";
import type { CriarObraAcaoProps } from "./types-criar-obra-acao";

export function useCriarObraAcao({ className, onCriada, onConcluida }: CriarObraAcaoProps) {
  const { state, salvarEtapa, definirJustificativaCondicoes, irParaEtapa } = useNovaObra();
  const { status, periodo, hoje, condicoes } = useCondicoesObra();
  const catalogo = useObraTiposObra();
  const toast = useToast();
  const emAndamento = useRef(false);
  const [salvando, setSalvando] = useState(false);
  const [justificando, setJustificando] = useState(false);
  const [divergencias, setDivergencias] = useState<DivergenciasAlocacao | null>(null);
  // Reenvio depois dos diálogos da equipe: a mesma justificativa de condições do envio anterior.
  const justificativaCondicoesEnviada = useRef<string | null>(null);

  const enviar = async (justificativa: string | null, estado: NovaObraState = state) => {
    if (emAndamento.current) return;
    emAndamento.current = true;
    justificativaCondicoesEnviada.current = justificativa;
    setSalvando(true);

    try {
      const resultado = await criarObra(montarPayloadObra(estado, justificativa));
      onCriada(
        CRIAR_OBRA.sucesso,
        resultado.codigo ? `ID da obra: ${resultado.codigo}` : undefined,
      );
      // O botão segue travado até a tela sair (sem criar duas vezes).
      onConcluida();
    } catch (error) {
      emAndamento.current = false;
      setSalvando(false);

      const divergenciasEquipe = isAPIError(error) ? readDivergenciasAlocacao(error) : null;
      if (divergenciasEquipe) {
        setDivergencias(divergenciasEquipe);
        return;
      }

      const { mensagem, detalhes } = lerErroCriarObra(error);
      toast.error(mensagem, detalhes.length > 0 ? detalhes.join(" ") : undefined, { life: 8000 });
    }
  };

  /** Divergência justificada: guarda a justificativa no wizard e reenvia o POST com ela. */
  const reenviarComJustificativaEquipe = (justificativa: string) => {
    const equipe = { ...readEquipe(state.etapas.equipe), justificativa };
    salvarEtapa("equipe", equipe);
    setDivergencias(null);
    void enviar(justificativaCondicoesEnviada.current, {
      ...state,
      etapas: { ...state.etapas, equipe },
    });
  };

  const criar = () => {
    if (status !== "ok") return;
    if (condicoes.exigeJustificativa && periodo) {
      setJustificando(true);
      return;
    }
    void enviar(null);
  };

  const fecharJustificativa = () => setJustificando(false);

  const ajustarDatas = () => {
    setJustificando(false);
    irParaEtapa(2);
  };

  const adicionarTipo = () => {
    setJustificando(false);
    irParaEtapa(3);
  };

  const fecharDivergencias = () => setDivergencias(null);

  const justificar = (justificativa: string) => {
    definirJustificativaCondicoes(justificativa);
    setJustificando(false);
    void enviar(justificativaParaEnvio(condicoes, justificativa));
  };

  return {
    state,
    justificar,
    status,
    periodo,
    hoje,
    condicoes,
    catalogo,
    salvando,
    justificando,
    fecharJustificativa,
    ajustarDatas,
    adicionarTipo,
    divergencias,
    fecharDivergencias,
    reenviarComJustificativaEquipe,
    criar,
    className,
  };
}
